import { z } from 'zod';
/**
 * What a reader of the visitor app's notification settings sees (docs/008 S7 — an
 * allow-list, not a row). `id`, `tenantId` and `createdAt` are deliberately absent: the
 * caller already knows its own tenant, and the row's identity is an audit-trail concern.
 *
 * `updatedAt` is NULLABLE because a tenant that has never changed the setting has NO ROW —
 * the DTO reports the defaults rather than inventing a timestamp for a decision nobody made.
 *
 * TWO CHANNELS, TWO SWITCHES, TWO DIFFERENT DEFAULTS (`hostEmailEnabled` on,
 * `hostSmsEnabled` off). Both defaults live in `toVisitorNotificationSettingDTO` and nowhere
 * else; see the `VisitorNotificationSetting` model comment in schema.prisma for why a text
 * is opt-in and a mail is not.
 *
 * `smsQuota` RIDES ON THIS DTO RATHER THAN A ROUTE OF ITS OWN, and that is a considered
 * choice, not a shortcut: it is one card showing one number, read exactly when the settings
 * screen is read, by the same caller, at the same permission (`visitor.settings.read`). A
 * second `GET` plus a second SDK resource plus a second `useQuery` hook would exist to
 * deliver a single `{ sent, limit }` pair that is only ever rendered next to the SMS switch
 * this same DTO already carries — two moving parts, twice the cache-invalidation surface, for
 * no reader who does not already have this object in hand. It is READ-ONLY: the tenant
 * cannot set its own cap (that is the `visitor.host_sms_monthly` entitlement, an operator's
 * call, not this settings page's), so it is absent from `updateVisitorNotificationSettingSchema`
 * below on purpose — a field a PATCH body accepted but silently ignored would be worse than
 * one it never mentions.
 */
export const visitorNotificationSettingDTOSchema = z.object({
    hostEmailEnabled: z.boolean(),
    hostSmsEnabled: z.boolean(),
    /** Show the visitor's photo in the host-arrival EMAIL (never SMS). Default `false` — PII, opt-in. */
    hostEmailIncludePhoto: z.boolean(),
    /** Direct-message the host in Slack/Teams on arrival (PRD-010 slice 2). Default `true`. */
    hostChatEnabled: z.boolean(),
    /**
     * Email the host when Slack/Teams cannot reach them while host email is off (PRD-010
     * CHAT-12). Default `true`; inert while email is on or chat is off.
     */
    hostChatFallbackToEmail: z.boolean(),
    /** Push the host's InstaCheckin Express app on arrival (S48-lh45). Default `true`. Acts only while `hostPushAvailable`. */
    hostPushEnabled: z.boolean(),
    /** READ-ONLY: the tenant's plan includes InstaCheckin Express (`visitor.mobile_app`). The settings page disables the switch without it. */
    hostPushAvailable: z.boolean(),
    /**
     * READ-ONLY: whether this tenant has an ACTIVE chat integration (Settings → Integrations).
     * The settings screen disables the chat switch without one. Rides on this DTO for the reason
     * `smsQuota` does — one card, one read — and because a visitor-settings reader does not hold
     * `platform.integrations.read`.
     */
    chatIntegrationConnected: z.boolean(),
    updatedAt: z.string().datetime().nullable(),
    /**
     * This tenant's host-arrival-SMS spend for the current UTC calendar month
     * (`visitor.host_sms_monthly`, S28). `period` is `YYYY-MM`. `limit: null` means
     * UNLIMITED and a reader must never render that as zero — the same rule
     * `EntitlementDTO.limit` carries in `entitlement.ts`, because this is that same
     * resolved entitlement, not a second cap invented here.
     */
    smsQuota: z.object({
        period: z.string(),
        sent: z.number().int().min(0),
        limit: z.number().int().min(0).nullable(),
    }),
});
/**
 * `PATCH /api/web/visitor/notification-settings`.
 *
 * BOTH FIELDS OPTIONAL, AT LEAST ONE REQUIRED — a genuine partial, which the single-field
 * version of this schema deliberately was not. The rule it replaces ("one required field, no
 * partial") existed when there was one switch and a PATCH could not be partial without being
 * empty. With two independent switches on one card, demanding both would mean every toggle
 * echoes back the value of the OTHER switch as the client last read it — and a client whose
 * read is stale then silently reverts a change someone else made. The `refine` keeps the half
 * of the old rule that still holds: an empty body is a caller bug, not a no-op, and Zod
 * rejecting it is cheaper than a service that has to decide what "change nothing" means.
 *
 * NOT `z.coerce.boolean()` — that reads the string `'false'` as `true`, which is exactly
 * how a tenant would end up mailing (or texting, at their own expense) hosts it had asked
 * not to. The same reasoning that made `SMTP_SECURE` a string enum in S12-TASK-010.
 *
 * `.strict()` so an unknown key is a 400 rather than a silent no-op: with two optional
 * fields, a typo'd `hostSMSEnabled` would otherwise parse, satisfy the refine whenever the
 * other field was sent, and change nothing while answering 200.
 */
export const updateVisitorNotificationSettingSchema = z
    .object({
    hostEmailEnabled: z.boolean().optional(),
    hostSmsEnabled: z.boolean().optional(),
    hostEmailIncludePhoto: z.boolean().optional(),
    hostChatEnabled: z.boolean().optional(),
    hostChatFallbackToEmail: z.boolean().optional(),
    hostPushEnabled: z.boolean().optional(),
})
    .strict()
    .refine((value) => value.hostEmailEnabled !== undefined ||
    value.hostSmsEnabled !== undefined ||
    value.hostEmailIncludePhoto !== undefined ||
    value.hostChatEnabled !== undefined ||
    value.hostChatFallbackToEmail !== undefined ||
    value.hostPushEnabled !== undefined, { message: 'at least one switch must be present' });
//# sourceMappingURL=visitor-notification-setting.js.map